import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors
} from '@dnd-kit/core'

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'

import { DragHandle, ZoneItem } from './widgets'
import { DragContextProps } from './widgets/DragContext'

type Props<T> = {
  items: T[]
  getSortableItem: (item: T, index: number) => UniqueIdentifier
  children?:
    | ((props: {
        item: T
        dragHandle: React.ReactNode
        contextValue: DragContextProps
      }) => React.ReactNode)
    | React.ReactNode
  onDragEnd?: (fn: (state: T[]) => T[], ev: DragEndEvent) => void
}

export function DndZone<T>({
  items,
  children,
  getSortableItem,
  onDragEnd
}: Props<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = (ev: DragEndEvent) => {
    const { active, over } = ev

    if (active.id !== over?.id) {
      onDragEnd?.((state) => {
        const activeIndex = state.findIndex(
          (record, index) => getSortableItem(record, index) === active?.id
        )
        const overIndex = state.findIndex(
          (record, index) => getSortableItem(record, index) === over?.id
        )

        return arrayMove(state, activeIndex, overIndex)
      }, ev)
    }
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext items={items.map(getSortableItem)}>
        {typeof children === 'function'
          ? items.map((item, index) => (
              <ZoneItem
                id={getSortableItem(item, index)}
                key={getSortableItem(item, index)}
              >
                {(contextValue) =>
                  children?.({ item, dragHandle: <DragHandle />, contextValue })
                }
              </ZoneItem>
            ))
          : children}
      </SortableContext>
    </DndContext>
  )
}

DndZone.ZoneItem = ZoneItem
